import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, ImagePlus, Link2, Loader2, Search, Star } from "lucide-react";
import api from "../../lib/api";

// Internal Admin -> Master Product Images (Sep 2026).
//
// Rudras-AI's OWN product images: a super admin enters a Master Product
// ID, sees its images, uploads more, marks the primary, retires one, and
// — manually — associates an image with one tenant product.
//
// Deliberately small. No gallery, ordering, cropping or tagging, and no
// tenant-facing counterpart: tenants never browse or pick these.
//
// SECURITY: every action here is refused server-side (403) for anyone
// but a super admin (`get_super` on every route). The `canManage` prop
// only spares other internal roles a page of controls that would fail.
// See Technical Handbook, Volume 4 §2.7.11.

const BASE = "/internal-admin/master-product-images";
const ACCEPT = "image/jpeg,image/png,image/webp";
const MASTER_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function normalizeMasterProductId(raw) {
  const value = String(raw || "").trim().toLowerCase();
  return MASTER_ID_RE.test(value) ? value : "";
}

export function apiErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  if (typeof detail === "string" && detail) return detail;
  if (err?.response?.status === 403) return "Only a super admin can manage Master images.";
  return fallback;
}

const SOURCE_LABEL = {
  none: "No photo",
  external_url: "External image address",
  tenant_upload: "Photo uploaded by the tenant",
  rudras_master: "Rudras Master image",
};

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(0)} KB`;
}

export function formatPrice(price, currency) {
  if (price === null || price === undefined || price === "") return "";
  const n = Number(price);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(n);
  } catch {
    return n.toFixed(2);
  }
}

// Cascading Tenant -> Catalog -> Product picker (Sep 2026). The super
// admin chooses by NAME; the canonical company / catalog / product ids
// the server listed are what get sent. Every level is scoped server-side
// by the level above, and the association re-validates all three.
function AssociatePanel({ image, onDone, onCancel }) {
  const [tenantQuery, setTenantQuery] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [productsTruncated, setProductsTruncated] = useState(false);
  const [productId, setProductId] = useState("");
  const [target, setTarget] = useState(null);
  const [replaceTenant, setReplaceTenant] = useState(false);
  const [loading, setLoading] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // The CURRENT selection, readable from async callbacks: a response for
  // a tenant / catalog / product the admin has since changed is dropped.
  const sel = useRef({ tenantId: "", catalogId: "", productId: "" });
  // Latest-search-wins for the two searchable lists: an older search
  // finishing late can never overwrite a newer one.
  const tenantSeq = useRef(0);
  const productSeq = useRef(0);
  // The chosen tenant / product are remembered as objects, so a later
  // search that no longer lists them never blanks the selection or makes
  // the confirmation fall back to an id.
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const tenant = selectedTenant;
  const catalog = catalogs.find((c) => c.catalog_id === catalogId) || null;
  const tenantOptions = selectedTenant && !tenants.some((t) => t.company_id === selectedTenant.company_id)
    ? [selectedTenant, ...tenants] : tenants;
  const productOptions = selectedProduct && !products.some((p) => p.product_id === selectedProduct.product_id)
    ? [selectedProduct, ...products] : products;
  const doneLoading = (key) => setLoading((cur) => (cur === key ? "" : cur));

  const searchTenants = useCallback(async (q) => {
    const seq = ++tenantSeq.current;
    setLoading("tenants");
    setErr("");
    try {
      const { data } = await api.get(`${BASE}/association/tenants`, { params: { q } });
      if (seq !== tenantSeq.current) return;
      setTenants(data.tenants || []);
    } catch (e) {
      if (seq === tenantSeq.current) setErr(apiErrorMessage(e, "Tenants could not be loaded."));
    } finally {
      setLoading((cur) => (cur === "tenants" ? "" : cur));
    }
  }, []);

  useEffect(() => { searchTenants(""); }, [searchTenants]);

  const loadProducts = async (companyId, catId, q) => {
    const seq = ++productSeq.current;
    setLoading("products");
    try {
      const { data } = await api.get(
        `${BASE}/association/tenants/${encodeURIComponent(companyId)}/catalogs/${encodeURIComponent(catId)}/products`,
        { params: { q } },
      );
      if (seq !== productSeq.current) return;
      if (sel.current.tenantId !== companyId || sel.current.catalogId !== catId) return;
      setProducts(data.products || []);
      setProductsTruncated(Boolean(data.truncated));
    } catch (e) {
      if (seq === productSeq.current && sel.current.catalogId === catId) {
        setErr(apiErrorMessage(e, "Products could not be loaded."));
      }
    } finally {
      doneLoading("products");
    }
  };

  const chooseTenant = async (companyId) => {
    // Changing the tenant clears everything below it.
    sel.current = { tenantId: companyId, catalogId: "", productId: "" };
    setTenantId(companyId);
    setSelectedTenant(tenantOptions.find((t) => t.company_id === companyId) || null);
    setSelectedProduct(null);
    productSeq.current += 1;
    setCatalogs([]); setCatalogId("");
    setProducts([]); setProductId(""); setProductQuery(""); setProductsTruncated(false);
    setTarget(null); setReplaceTenant(false); setErr("");
    if (!companyId) return;
    setLoading("catalogs");
    try {
      const { data } = await api.get(`${BASE}/association/tenants/${encodeURIComponent(companyId)}/catalogs`);
      if (sel.current.tenantId !== companyId) return;
      setCatalogs(data.catalogs || []);
    } catch (e) {
      if (sel.current.tenantId === companyId) setErr(apiErrorMessage(e, "Catalogs could not be loaded."));
    } finally {
      doneLoading("catalogs");
    }
  };

  const chooseCatalog = async (catId) => {
    // Changing the catalog clears the product.
    sel.current = { ...sel.current, catalogId: catId, productId: "" };
    setCatalogId(catId);
    setSelectedProduct(null);
    productSeq.current += 1;
    setProducts([]); setProductId(""); setProductQuery(""); setProductsTruncated(false);
    setTarget(null); setReplaceTenant(false); setErr("");
    if (catId) await loadProducts(sel.current.tenantId, catId, "");
  };

  const chooseProduct = async (pid) => {
    sel.current = { ...sel.current, productId: pid };
    setProductId(pid);
    setSelectedProduct(productOptions.find((p) => p.product_id === pid) || null);
    setTarget(null); setReplaceTenant(false); setErr("");
    if (!pid) return;
    const ids = { company_id: sel.current.tenantId, catalog_id: sel.current.catalogId, product_id: pid };
    setLoading("target");
    try {
      const { data } = await api.get(`${BASE}/association-target`, { params: ids });
      if (sel.current.productId !== pid) return;
      setTarget(data.target);
    } catch (e) {
      if (sel.current.productId === pid) setErr(apiErrorMessage(e, "That product could not be looked up."));
    } finally {
      doneLoading("target");
    }
  };

  const associate = async () => {
    if (!target || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`${BASE}/${image.id}/associate`, {
        company_id: target.company_id,
        catalog_id: target.catalog_id,
        product_id: target.product_id,
        expected_image_url: target.image_url || "",
        replace_tenant_image: replaceTenant,
      });
      onDone(data);
    } catch (e2) {
      setErr(apiErrorMessage(e2, "The image could not be associated."));
    } finally {
      setBusy(false);
    }
  };

  const needsConsent = target?.image_source === "tenant_upload";
  const selectCls = "h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm disabled:opacity-50";

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="mpi-associate-panel">
      <div className="text-sm font-medium">Use this image on one tenant product</div>
      <p className="text-xs text-muted-foreground">
        Manual association. The tenant can display this image but never owns it: removing it from
        their product, or uploading their own photo, never deletes the Master image.
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor={`mpi-tenant-${image.id}`}>1. Tenant</label>
          <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); searchTenants(tenantQuery.trim()); }}>
            <input aria-label="Search tenants" placeholder="Search by name" value={tenantQuery}
              onChange={(e) => setTenantQuery(e.target.value)}
              className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 px-2 text-sm" data-testid="mpi-assoc-tenant-search"/>
            <button type="submit" className="h-9 px-2 rounded-md border bg-white" aria-label="Search tenants"
              data-testid="mpi-assoc-tenant-search-btn"><Search className="w-4 h-4"/></button>
          </form>
          <select id={`mpi-tenant-${image.id}`} value={tenantId} onChange={(e) => chooseTenant(e.target.value)}
            className={selectCls} data-testid="mpi-assoc-tenant">
            <option value="">{tenantOptions.length ? "Choose a tenant…" : "No tenants found"}</option>
            {tenantOptions.map((t) => (
              <option key={t.company_id} value={t.company_id}>
                {t.name}{t.customer_reference_id ? ` — ${t.customer_reference_id}` : ""}{t.is_enabled ? "" : " (disabled)"}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor={`mpi-catalog-${image.id}`}>2. Catalog</label>
          <select id={`mpi-catalog-${image.id}`} value={catalogId} disabled={!tenantId}
            onChange={(e) => chooseCatalog(e.target.value)} className={selectCls} data-testid="mpi-assoc-catalog">
            <option value="">{tenantId ? (catalogs.length ? "Choose a catalog…" : "No catalogs") : "Choose a tenant first"}</option>
            {catalogs.map((c) => (
              <option key={c.catalog_id} value={c.catalog_id}>
                {c.name}{c.display_code ? ` — ${c.display_code}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor={`mpi-product-${image.id}`}>3. Product</label>
          <form className="flex gap-1" onSubmit={(e) => {
            e.preventDefault();
            if (catalogId) loadProducts(tenantId, catalogId, productQuery.trim());
          }}>
            <input aria-label="Search products" placeholder="Search name or SKU" value={productQuery}
              disabled={!catalogId} onChange={(e) => setProductQuery(e.target.value)}
              className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 px-2 text-sm disabled:opacity-50"
              data-testid="mpi-assoc-product-search"/>
            <button type="submit" disabled={!catalogId} className="h-9 px-2 rounded-md border bg-white disabled:opacity-50"
              aria-label="Search products" data-testid="mpi-assoc-product-search-btn"><Search className="w-4 h-4"/></button>
          </form>
          <select id={`mpi-product-${image.id}`} value={productId} disabled={!catalogId}
            onChange={(e) => chooseProduct(e.target.value)} className={selectCls} data-testid="mpi-assoc-product">
            <option value="">{catalogId ? (productOptions.length ? "Choose a product…" : "No products") : "Choose a catalog first"}</option>
            {productOptions.map((p) => (
              <option key={p.product_id} value={p.product_id}>
                {p.name}{formatPrice(p.price, p.currency) ? ` — ${formatPrice(p.price, p.currency)}` : ""}{p.sku ? ` · SKU ${p.sku}` : ""}
              </option>
            ))}
          </select>
          {productsTruncated && (
            <div className="text-[11px] text-muted-foreground" data-testid="mpi-assoc-products-truncated">
              Showing the first matches — search to narrow the list.
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="mpi-assoc-loading">
          <Loader2 className="w-3 h-3 animate-spin"/>Loading…
        </div>
      )}

      {target && (
        <div className="rounded-md border border-slate-200 bg-white p-3 text-sm space-y-3" data-testid="mpi-assoc-target">
          <div className="font-medium">Confirm association</div>
          <dl className="grid grid-cols-[7rem_1fr] gap-x-2 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Tenant</dt>
            <dd data-testid="mpi-assoc-confirm-tenant">{tenant?.name || "(tenant)"}</dd>
            <dt className="text-muted-foreground">Catalog</dt>
            <dd data-testid="mpi-assoc-confirm-catalog">
              {target.catalog_name || catalog?.name}{(target.catalog_display_code || catalog?.display_code) ? ` — ${target.catalog_display_code || catalog?.display_code}` : ""}
            </dd>
            <dt className="text-muted-foreground">Product</dt>
            <dd data-testid="mpi-assoc-target-name">
              {target.name || "(unnamed product)"}{formatPrice(target.price, target.currency) ? ` — ${formatPrice(target.price, target.currency)}` : ""}
              {target.sku ? ` · SKU ${target.sku}` : ""}
            </dd>
          </dl>
          <div className="flex gap-6">
            <div className="text-xs space-y-1">
              <div className="text-muted-foreground">Current product image</div>
              {target.image_url ? (
                <img src={target.image_url} alt="" className="w-20 h-20 object-cover rounded border" data-testid="mpi-assoc-target-image"/>
              ) : (
                <div className="w-20 h-20 rounded border bg-slate-100 grid place-items-center text-muted-foreground"
                  data-testid="mpi-assoc-target-noimage">No photo</div>
              )}
              <div data-testid="mpi-assoc-target-source">{SOURCE_LABEL[target.image_source] || target.image_source}</div>
            </div>
            <div className="text-xs space-y-1">
              <div className="text-muted-foreground">Master image to associate</div>
              <img src={image.image_url} alt="" className="w-20 h-20 object-cover rounded border" data-testid="mpi-assoc-master-image"/>
              <div className="font-mono">{image.master_product_id}</div>
            </div>
          </div>
          {needsConsent && (
            <label className="flex items-start gap-2 text-xs text-amber-800" data-testid="mpi-assoc-consent">
              <input type="checkbox" checked={replaceTenant} onChange={(e) => setReplaceTenant(e.target.checked)}
                data-testid="mpi-assoc-consent-box"/>
              Replace the photo this tenant uploaded themselves. Their photo will be removed.
            </label>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={associate} disabled={busy || (needsConsent && !replaceTenant)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 text-white text-sm disabled:opacity-50"
              data-testid="mpi-assoc-confirm">
              <Link2 className="w-4 h-4"/>Associate image
            </button>
          </div>
        </div>
      )}
      <button type="button" onClick={onCancel} className="h-8 px-2 text-sm text-muted-foreground" data-testid="mpi-assoc-cancel">
        Cancel
      </button>
      {err && <div className="text-sm text-red-700" role="alert" data-testid="mpi-assoc-error">{err}</div>}
    </div>
  );
}

function ImageCard({ image, busy, onPrimary, onRetire, onAssociated }) {
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [associating, setAssociating] = useState(false);
  const [notice, setNotice] = useState("");
  const active = image.status === "active";

  return (
    <div className="rounded-xl border bg-white p-3" data-testid={`mpi-image-${image.id}`}>
      <div className="flex gap-3">
        {image.image_url ? (
          <img src={image.image_url} alt="" className="w-28 h-28 object-cover rounded-lg border bg-slate-50"
            data-testid={`mpi-thumb-${image.id}`}/>
        ) : (
          <div className="w-28 h-28 rounded-lg border bg-slate-100"/>
        )}
        <div className="text-sm space-y-1 min-w-0">
          <div className="flex flex-wrap gap-1.5">
            {image.is_primary && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs"
                data-testid={`mpi-primary-${image.id}`}>
                <Star className="w-3 h-3"/>Primary
              </span>
            )}
            <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-emerald-100 text-emerald-800" : image.status === "retired" ? "bg-slate-200 text-slate-700" : "bg-orange-100 text-orange-800"}`}
              data-testid={`mpi-status-${image.id}`}>
              {image.status === "pending" ? "Upload incomplete" : image.status === "retired" ? "Retired" : "Active"}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {image.width}×{image.height} · {formatBytes(image.bytes)} · uploaded {String(image.uploaded_at || "").slice(0, 10)}
          </div>
          <div className="text-xs" data-testid={`mpi-refs-${image.id}`}>
            Used by {image.reference_count} tenant product{image.reference_count === 1 ? "" : "s"}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono break-all">ID {image.id}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {active && !image.is_primary && (
          <button type="button" disabled={busy} onClick={() => onPrimary(image)}
            className="h-8 px-3 rounded-md border text-xs disabled:opacity-50" data-testid={`mpi-make-primary-${image.id}`}>
            Make primary
          </button>
        )}
        {active && (
          <button type="button" disabled={busy} onClick={() => { setAssociating((v) => !v); setNotice(""); }}
            className="h-8 px-3 rounded-md border text-xs disabled:opacity-50" data-testid={`mpi-associate-${image.id}`}>
            Associate with a tenant product…
          </button>
        )}
        {image.status !== "retired" && !confirmRetire && (
          <button type="button" disabled={busy} onClick={() => setConfirmRetire(true)}
            className="h-8 px-3 rounded-md border border-red-200 text-red-700 text-xs disabled:opacity-50"
            data-testid={`mpi-retire-${image.id}`}>
            Retire
          </button>
        )}
        {confirmRetire && (
          <span className="flex items-center gap-2 text-xs" data-testid={`mpi-retire-confirm-${image.id}`}>
            Retire this image? Products already using it keep showing it; it cannot be newly associated.
            <button type="button" disabled={busy} onClick={() => { setConfirmRetire(false); onRetire(image); }}
              className="h-8 px-3 rounded-md bg-red-600 text-white" data-testid={`mpi-retire-yes-${image.id}`}>
              Retire image
            </button>
            <button type="button" onClick={() => setConfirmRetire(false)} className="h-8 px-2"
              data-testid={`mpi-retire-cancel-${image.id}`}>
              Cancel
            </button>
          </span>
        )}
      </div>

      {associating && (
        <AssociatePanel
          image={image}
          onCancel={() => setAssociating(false)}
          onDone={(data) => {
            setAssociating(false);
            const name = data?.target?.name || "the product";
            setNotice(data?.already_associated
              ? `${name} already uses this image.`
              : `Associated with ${name}.${data?.warning ? " Note: this image was retired at the same moment." : ""}`);
            onAssociated();
          }}
        />
      )}
      {notice && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`mpi-notice-${image.id}`}>
          <CheckCircle2 className="w-4 h-4"/>{notice}
        </div>
      )}
    </div>
  );
}

export default function MasterProductImagesPanel({ canManage }) {
  const [input, setInput] = useState("");
  const [current, setCurrent] = useState("");
  const [images, setImages] = useState([]);
  const [products, setProducts] = useState([]);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  // Latest-request-wins. Every listing gets a sequence number and only
  // the newest one may update the page, so a slow response for Master
  // Product A can never overwrite (or switch the page back from) B.
  const loadSeqRef = useRef(0);
  const currentRef = useRef("");
  const includeRetiredRef = useRef(false);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get(`${BASE}/products`);
      setProducts(data.products || []);
    } catch {
      // The picker is a convenience; typing an ID always works.
    }
  }, []);

  const loadImages = useCallback(async (mid, withRetired) => {
    if (!mid) return;
    const seq = ++loadSeqRef.current;
    currentRef.current = mid;
    includeRetiredRef.current = withRetired;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(BASE, { params: { master_product_id: mid, include_retired: withRetired } });
      if (seq !== loadSeqRef.current) return;
      setImages(data.images || []);
      setCurrent(data.master_product_id);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setErr(apiErrorMessage(e, "Images could not be loaded."));
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { if (canManage) loadProducts(); }, [canManage, loadProducts]);

  if (!canManage) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="mpi-forbidden">
        Master Product Images are Rudras-AI platform assets. Only a super admin can manage them.
      </div>
    );
  }

  const open = (e) => {
    e?.preventDefault();
    const mid = normalizeMasterProductId(input);
    if (!mid) {
      setErr("Master Product IDs use lowercase letters, digits, '-' and '_' (up to 64 characters), e.g. basmati-rice-5lb.");
      return;
    }
    setInput(mid);
    loadImages(mid, includeRetired);
  };

  // Reloads whatever the admin is looking at NOW (refs), not what was
  // open when the action started.
  // Master Library export: one row per image, stable ids first. The
  // image URL column is a preview only — imports reference Master Image ID.
  const exportLibrary = async () => {
    setErr("");
    try {
      const r = await api.get(`${BASE}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "rudras-master-image-library.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(apiErrorMessage(e, "The Master Library could not be exported."));
    }
  };

  const refresh = () => { loadImages(currentRef.current, includeRetiredRef.current); loadProducts(); };

  const upload = async (file) => {
    if (!file || !current || busy) return;
    setBusy(true);
    setErr("");
    const form = new FormData();
    form.append("master_product_id", current);
    form.append("file", file);
    try {
      await api.post(BASE, form, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
    } catch (e) {
      setErr(apiErrorMessage(e, "The image could not be uploaded."));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const act = async (path, fallback) => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(path);
      refresh();
    } catch (e) {
      setErr(apiErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="mpi-panel">
      <form onSubmit={open} className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <div className="mb-1 font-medium">Master Product ID</div>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. basmati-rice-5lb"
            list="mpi-known-products" className="h-10 w-72 rounded-lg border border-slate-300 px-3 text-sm font-mono"
            data-testid="mpi-master-id"/>
          <datalist id="mpi-known-products">
            {products.map((p) => <option key={p.master_product_id} value={p.master_product_id}/>)}
          </datalist>
        </label>
        <button type="submit" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm" data-testid="mpi-open">
          Open
        </button>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground ml-2">
          <input type="checkbox" checked={includeRetired} data-testid="mpi-include-retired"
            onChange={(e) => { setIncludeRetired(e.target.checked); if (current) loadImages(current, e.target.checked); }}/>
          Show retired images
        </label>
        <button type="button" onClick={exportLibrary}
          className="ml-auto inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border text-sm"
          data-testid="mpi-export">
          <Download className="w-4 h-4"/>Export Master Library (CSV)
        </button>
      </form>

      {products.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="mpi-known-list">
          {products.map((p) => (
            <button key={p.master_product_id} type="button"
              onClick={() => { setInput(p.master_product_id); loadImages(p.master_product_id, includeRetired); }}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-mono ${p.master_product_id === current ? "bg-slate-900 text-white" : "bg-white"}`}
              data-testid={`mpi-known-${p.master_product_id}`}>
              {p.master_product_id} ({p.active_count})
            </button>
          ))}
        </div>
      )}

      {err && <div className="text-sm text-red-700" role="alert" data-testid="mpi-error">{err}</div>}

      {current && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold" data-testid="mpi-current">{current}</h2>
            <label className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}
              data-testid="mpi-upload-label">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ImagePlus className="w-4 h-4"/>}
              {images.length ? "Upload another image" : "Upload image"}
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" disabled={busy}
                onChange={(e) => upload(e.target.files?.[0])} data-testid="mpi-upload-input"/>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            JPEG, PNG or WebP, under 5 MB. Images are converted to WebP and resized to at most 800 px.
            The first image becomes the primary image.
          </p>
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
          ) : images.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="mpi-empty">No images yet for this Master Product.</div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="mpi-images">
              {images.map((img) => (
                <ImageCard key={img.id} image={img} busy={busy}
                  onPrimary={(i) => act(`${BASE}/${i.id}/primary`, "The primary image could not be changed.")}
                  onRetire={(i) => act(`${BASE}/${i.id}/retire`, "The image could not be retired.")}
                  onAssociated={refresh}/>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
